import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  SpecialRequestType,
  SpecialRequestTypesOut,
  SpecialRequestsReservation,
  SpecialRequestsRoom,
  SpecialRequestsRoomIn
} from '@appTypes/api.types';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';
import { environment } from '@env/environment';
import { Observable, catchError, map, throwError } from 'rxjs';

/** A coded refusal of the special requests API (QA doc docs features/booking-special-requests/qa.md § Error codes, rule C3 / C17 / C23). */
export interface SpecialRequestsError {
  code: string;
  traceId: string | null;
}

/** The codes the contract declares (openapi 0.2.0-draft); anything else is mapped by HTTP status. */
const KNOWN_CODES = new Set([
  'REQUEST_LIST_UNAVAILABLE',
  'REQUESTS_UNAVAILABLE',
  'RESERVATION_NOT_FOUND',
  'ROOM_NOT_FOUND',
  'OTHER_WISHES_TOO_LONG',
  'LINK_INVALID',
  'REQUEST_TYPE_NOT_AVAILABLE',
  'REQUEST_INVALID',
  'REQUESTS_LOCKED',
  'REQUESTS_CHANGED',
  'FEATURE_FLAG_OFF',
  'TOO_MANY_REQUESTS'
]);

/**
 * Booking special requests, website side (QA doc docs features/booking-special-requests/qa.md R-SR6 .. R-SR19g, R-SR45):
 * online-api `special-requests/types`, `reservations/{rfid}/special-requests` and the per-room PUT. Every failure reaches the screen as
 * `{ code, traceId }` - a code the contract names passes through; no answer / 5xx is the outage code of the call (C17), never a stale list.
 */
@Injectable({ providedIn: 'root' })
export class SpecialRequestsApiService {
  readonly #http = inject(HttpClient);
  readonly #api = environment.urls.api;

  /** The active list in its sort order (R-SR6). An empty list = the feature is hidden (R-SR45). */
  types(): Observable<SpecialRequestType[]> {
    return this.#http.get<SpecialRequestTypesOut>(`${this.#api}/special-requests/types`).pipe(
      map((r) => [...(r?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.typeId - b.typeId)),
      coded('REQUEST_LIST_UNAVAILABLE')
    );
  }

  /** The rooms of one reservation with their saved requests (R-SR14, R-SR15, R-SR17 .. R-SR19a). */
  get(rfid: string): Observable<SpecialRequestsReservation> {
    return this.#http
      .get<SpecialRequestsReservation>(`${this.#api}/reservations/${encodeURIComponent(rfid)}/special-requests`)
      .pipe(coded('REQUESTS_UNAVAILABLE'));
  }

  /** Saves one room (R-SR14, R-SR16, R-SR19b .. R-SR19d, R-SR19g); the answer is the room as saved, with its new row version. */
  saveRoom(rfid: string, roomId: string, body: SpecialRequestsRoomIn): Observable<SpecialRequestsRoom> {
    return this.#http
      .put<SpecialRequestsRoom>(
        `${this.#api}/reservations/${encodeURIComponent(rfid)}/rooms/${encodeURIComponent(roomId)}/special-requests`,
        body
      )
      .pipe(coded('REQUESTS_UNAVAILABLE'));
  }
}

/** True for a value `coded()` already produced: a string `code`, a `traceId` key and none of the HTTP error's own fields. */
function isCoded(e: unknown): e is SpecialRequestsError {
  const r = e as Partial<SpecialRequestsError> & { status?: unknown; errorCode?: unknown; error?: unknown };
  return (
    !!r &&
    typeof r === 'object' &&
    typeof r.code === 'string' &&
    'traceId' in r &&
    r.status === undefined &&
    r.errorCode === undefined &&
    r.error === undefined
  );
}

/**
 * Maps an `ApiErrorResponse` (or any thrown value) to `{ code, traceId }`. Exported for the specs. Maps ONCE (SRDIALOG-002, R-SDA18):
 * the api service already ends every call in `coded()`, so a value that is already `{ code, traceId }` is returned as it is (a code the contract does not name becomes the fallback, the Trace ID is kept) - a second
 * mapping found none of the HTTP error's fields and turned every refusal into the fallback without its Trace ID.
 */
export function toSpecialRequestsError(e: unknown, fallback: string): SpecialRequestsError {
  if (isCoded(e)) return { code: KNOWN_CODES.has(e.code) ? e.code : fallback, traceId: e.traceId };
  const r = e as ApiErrorResponse<{ errorCode?: string; traceId?: string }> | null;
  const raw = r?.error?.errorCode ?? r?.errorCode ?? '';
  let code = KNOWN_CODES.has(raw) ? raw : '';
  if (!code) {
    if (r?.status === 403 || r?.status === 404) code = 'RESERVATION_NOT_FOUND';
    else if (r?.status === 429) code = 'TOO_MANY_REQUESTS';
    else code = fallback;
  }
  return { code, traceId: r?.error?.traceId ?? r?.activityId ?? null };
}

function coded<T>(fallback: string) {
  return catchError<T, Observable<never>>((e: unknown) => throwError(() => toSpecialRequestsError(e, fallback)));
}
